import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { firestore } from "../../firebaseConfig";

const toMs = (e) => e.timestamp?.toMillis?.() || Date.parse(e.date || "") || 0;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One entry per conversation (latest message wins), newest first. */
export const groupConversations = (emails) => {
  const byThread = new Map();
  emails.filter(e => !e.deleted).forEach(e => {
    const key = e.threadKey || e.threadId || e.id;
    const prev = byThread.get(key);
    if (!prev) byThread.set(key, { latest: e, count: 1 });
    else byThread.set(key, { latest: toMs(e) > toMs(prev.latest) ? e : prev.latest, count: prev.count + 1 });
  });
  return [...byThread.values()].sort((a, b) => toMs(b.latest) - toMs(a.latest));
};

const formatDate = (ms) => (ms
  ? new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: new Date(ms).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined })
  : "");

/**
 * Three explicit steps before anything opens in the inbox:
 * which conversation → to whom → blank forward or template cover text.
 */
const ForwardWizard = ({ worldId, contactEmail, colleagues, templates, onRun }) => {
  const [conversations, setConversations] = useState(null);
  const [emailId, setEmailId] = useState("");
  const [to, setTo] = useState({ email: "", name: "" });
  const [typed, setTyped] = useState("");
  const [mode, setMode] = useState("");
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    if (!worldId || !contactEmail) return undefined;
    let cancelled = false;
    getDocs(query(
      collection(firestore, "world_inbox", worldId, "emails"),
      where("fromEmail", "==", contactEmail.toLowerCase()),
      limit(50),
    ))
      .then(snap => {
        if (cancelled) return;
        const list = groupConversations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setConversations(list);
        if (list.length === 1) setEmailId(list[0].latest.id);
      })
      .catch(err => {
        console.error("Error loading conversations:", err);
        if (!cancelled) setConversations([]);
      });
    return () => { cancelled = true; };
  }, [worldId, contactEmail]);

  const selected = useMemo(() => conversations?.find(c => c.latest.id === emailId) || null, [conversations, emailId]);
  const typedValid = EMAIL_RE.test(typed.trim()) && typed.trim().toLowerCase() !== contactEmail.toLowerCase();
  const recipient = to.email || (typedValid ? typed.trim() : "");
  const ready = selected && recipient && (mode === "blank" || (mode === "template" && templateId));

  const submit = () => {
    if (!ready) return;
    onRun({
      kind: "forward_thread",
      emailId,
      forwardTo: recipient,
      forwardToName: to.email ? to.name : "",
      templateId: mode === "template" ? templateId : "",
    });
  };

  if (conversations === null) {
    return <div className="tp-panel tp-fw"><div className="tp-fw-empty"><i className="fa fa-spinner fa-spin"></i> Loading conversations…</div></div>;
  }
  if (!conversations.length) {
    return (
      <div className="tp-panel tp-fw">
        <div className="tp-fw-empty"><i className="fa fa-inbox"></i> No conversation from {contactEmail} in your inbox yet — nothing to forward.</div>
      </div>
    );
  }

  return (
    <div className="tp-panel tp-fw">
      <div className="tp-fw-step">
        <div className="tp-fw-label">Which conversation</div>
        <div className="tp-fw-convos">
          {conversations.map(({ latest, count }) => (
            <button
              key={latest.id}
              type="button"
              className={`tp-fw-convo ${emailId === latest.id ? "is-active" : ""}`}
              onClick={() => setEmailId(latest.id)}
            >
              <div className="tp-fw-convo-top">
                <strong>{latest.subject || "(no subject)"}</strong>
                <span>{formatDate(toMs(latest))}</span>
              </div>
              <p>{latest.preview || (latest.bodyText || "").slice(0, 140)}</p>
              {count > 1 && <em><i className="fa fa-comments"></i> {count} messages</em>}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="tp-fw-step">
          <div className="tp-fw-label">Forward to</div>
          <div className="tp-fw-row">
            {colleagues.map(d => (
              <button
                key={d.email}
                type="button"
                className={`tp-chip ${to.email === d.email ? "tp-chip--active" : ""}`}
                onClick={() => { setTo(to.email === d.email ? { email: "", name: "" } : { email: d.email, name: d.name || "" }); setTyped(""); }}
              >
                <i className="fa fa-user-tie"></i> {d.name || d.email}{(d.position || d.role) ? <em> · {d.position || d.role}</em> : null}
              </button>
            ))}
            <input
              type="email"
              className="tp-fw-input"
              placeholder="or type colleague@company.com"
              value={typed}
              onChange={e => { setTyped(e.target.value); setTo({ email: "", name: "" }); }}
            />
          </div>
        </div>
      )}

      {selected && recipient && (
        <div className="tp-fw-step">
          <div className="tp-fw-label">How should it go out</div>
          <div className="tp-fw-modes">
            <button type="button" className={`tp-fw-mode ${mode === "blank" ? "is-active" : ""}`} onClick={() => setMode("blank")}>
              <strong>New blank email</strong>
              <small>Conversation attached — you write the cover text</small>
            </button>
            <button
              type="button"
              className={`tp-fw-mode ${mode === "template" ? "is-active" : ""}`}
              onClick={() => setMode("template")}
              disabled={!templates.length}
              title={!templates.length ? "No custom templates yet" : undefined}
            >
              <strong>Template email</strong>
              <small>AI writes the cover text — conversation attached</small>
            </button>
          </div>
          {mode === "template" && (
            <div className="tp-fw-row">
              {templates.map(t => (
                <button key={t.id} type="button" className={`tp-chip ${templateId === t.id ? "tp-chip--active" : ""}`} onClick={() => setTemplateId(t.id)}>
                  <i className="fa fa-file-lines"></i> {t.topic}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {ready && (
        <div className="tp-fw-foot">
          <span>
            <i className="fa fa-share"></i> “{selected.latest.subject || "(no subject)"}” → {to.name || recipient}
            {mode === "template" ? " · with template text" : " · blank"}
          </span>
          <button type="button" className="altBtn altBtn--save" onClick={submit}>
            <i className="fa fa-arrow-right"></i> Open in inbox
          </button>
        </div>
      )}
    </div>
  );
};

export default ForwardWizard;
